import { ContextMenu, DropdownMenu } from "radix-ui";
import { Icon } from "./Icon.tsx";
import { cx } from "./tokens.ts";
import { isMenuSeparator, type MenuEntry } from "./types.ts";

/** The parts of a Radix menu family (DropdownMenu and ContextMenu share the same item API). */
type MenuKit = Pick<typeof DropdownMenu, "Item" | "CheckboxItem" | "ItemIndicator" | "Separator" | "Label">;

const kits: Record<"dropdown" | "context", MenuKit> = { dropdown: DropdownMenu, context: ContextMenu as unknown as MenuKit };

/**
 * The rows of a `Menu` or a `Pressable.contextMenu`: items (icon, label, trailing hint or icon, destructive, disabled), toggles
 * (`selected` defined: a trailing checkmark, menuitemcheckbox) and separators. Items get `data-testid="menu-<id>"`.
 * The icon column is kept only when at least one item has an icon, so labels line up.
 */
export function MenuEntries({ kind, entries }: { kind: "dropdown" | "context"; entries: readonly MenuEntry[] }) {
  const K = kits[kind];
  const iconColumn = entries.some((e) => !isMenuSeparator(e) && e.icon !== undefined);
  return entries.map((e) => {
    if (isMenuSeparator(e)) return e.heading === undefined ? <K.Separator key={e.id} className="b-menu-separator" /> : <K.Label key={e.id} className="b-menu-heading">{e.heading}</K.Label>;
    const cls = cx("b-menu-item", e.destructive === true && "b-destructive", e.description !== undefined && "b-menu-item-described");
    const body = (
      <>
        {e.icon !== undefined ? <Icon name={e.icon} size="icon.sm" /> : iconColumn ? <span className="b-menu-icon-space" /> : null}
        {e.description === undefined ? (
          <span className="b-menu-label">{e.label}</span>
        ) : (
          <span className="b-menu-text">
            <span className="b-menu-label">{e.label}</span>
            <span className="b-menu-description">{e.description}</span>
          </span>
        )}
        {e.hint === undefined ? null : <span className="b-menu-hint">{e.hint}</span>}
        {e.trailingIcon === undefined ? null : <Icon name={e.trailingIcon} size="icon.sm" color="text.tertiary" />}
      </>
    );
    if (e.selected !== undefined) {
      return (
        <K.CheckboxItem key={e.id} className={cls} checked={e.selected} disabled={e.disabled} onSelect={e.onSelect} data-testid={`menu-${e.id}`}>
          {body}
          <K.ItemIndicator className="b-menu-check">
            <Icon name="check" size="icon.sm" />
          </K.ItemIndicator>
        </K.CheckboxItem>
      );
    }
    return (
      <K.Item key={e.id} className={cls} disabled={e.disabled} onSelect={e.onSelect} data-testid={`menu-${e.id}`}>
        {body}
      </K.Item>
    );
  });
}
